import { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { ReportCreatePageQuery } from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { useAutoOpenChat } from 'modules/ai/hooks/useAutoOpenChat'
import { getRenderedDefFromTableId } from 'modules/ai_reports/components/DatasourceTypeRenderer/tableRendererRegistry'
import { DateNavigationFilter } from 'modules/ai_reports/components/DateNavigationFilter'
import { Layout } from 'modules/ai_reports/components/Layout'
import { ReportWidget } from 'modules/ai_reports/components/ReportWidget/ReportWidget'
import { SaveModal } from 'modules/ai_reports/components/SaveModal'
import { useReportState } from 'modules/ai_reports/hooks/useReportState'
import { assertDefined } from 'modules/ai_reports/lib/assertDefined'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export const AiReportsMainContent = ({
  sourceId,
  datasource,
}: {
  sourceId: string
  datasource: NonNullable<ReportCreatePageQuery['iris']['factTable']>
}) => {
  assertDefined(sourceId, 'Source ID is required')

  const rendererDef = getRenderedDefFromTableId(sourceId)
  const dateNavigatorConfig = rendererDef?.dateNavigatorConfig?.({})

  const initialDateFilter = useMemo(() => {
    if (dateNavigatorConfig?.defaultValue instanceof Date) {
      return { from: dateNavigatorConfig.defaultValue, to: dateNavigatorConfig.defaultValue }
    }
    return dateNavigatorConfig?.defaultValue
  }, [dateNavigatorConfig?.defaultValue])

  const reportName = 'AI Report'

  const schema = useUnsafeMemo(
    () => datasource.columnsConnection.nodes.map((node) => node.schema),
    [datasource.columnsConnection.nodes]
  )

  // Use unified state management hook
  const { state, dispatch, agentState, NavConfirmModal, saveReport, handleExport } = useReportState(
    {
      datasourceId: sourceId,
      schema,
      initialDateFilter,
      reportName,
      datasourceName: datasource.name.trim(),
    }
  )

  useAutoOpenChat()

  const defaultSqlQuery = `SELECT * FROM \`${sourceId}\``

  return (
    <>
      {/* This is a hack to make the page full height and to use the responsive page body to get the header action clicks*/}
      <Box className="hidden">
        <ResponsivePage.Body
          onActionClick={(key: string) => {
            if (key === 'save') {
              dispatch({ type: 'UNSAVED_CHANGES_CONFIRM_REQUESTED' })
            } else if (key === 'export') {
              handleExport()
            }
          }}
          variant="fullscreen"
        >
          &nbsp;
        </ResponsivePage.Body>
      </Box>
      <NavConfirmModal />
      <SaveModal
        open={state.saveModalOpen}
        onClose={() => dispatch({ type: 'UNSAVED_CHANGES_DISMISSED' })}
        onClickSave={saveReport}
        name={agentState?.sqlAnswer?.title}
        description={agentState?.sqlAnswer?.query_summary}
      />
      <Layout
        header={
          dateNavigatorConfig && (
            <DateNavigationFilter
              config={dateNavigatorConfig}
              onSelect={(value) => {
                dispatch({ type: 'DATE_FILTER_UPDATED', payload: value?.value })
              }}
              value={
                state.dateFilter
                  ? {
                      value: {
                        from: state.dateFilter.from,
                        to: state.dateFilter.to ?? state.dateFilter.from,
                      },
                      granularity: dateNavigatorConfig.granularity,
                    }
                  : undefined
              }
            />
          )
        }
      >
        <ReportWidget
          currentFilters={state.datatableFilters ?? undefined}
          sqlQuery={state.sqlQuery}
          description={state.description}
          dateFilter={state.dateFilter}
          tableId={sourceId ?? ''}
          id={Math.random().toString(36).substring(2, 15)}
          columnFormatHints={agentState?.sqlAnswer?.column_format_hints || []}
          reportName={reportName}
          hideMetadata={state.sqlQuery === defaultSqlQuery}
          onStateChange={(s) => {
            if (s.filters) {
              dispatch({ type: 'DATACOLLECTION_FILTERS_UPDATED', payload: s.filters })
            }
          }}
          schema={schema}
        />
      </Layout>
    </>
  )
}
